
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Plus, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ErrorBoundary } from "./ErrorBoundary";

interface AddRestrictionFormData {
  type: string;
  title: string;
  description: string;
  category: string;
  default_value: string;
  placement: string;
  parent_id?: string;
}

const AddRestrictionForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isLoading 
}: {
  formData: AddRestrictionFormData;
  setFormData: React.Dispatch<React.SetStateAction<AddRestrictionFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isLoading: boolean;
}) => {
  console.log('AddRestrictionForm rendering with:', { formData });

  return (
    <form onSubmit={onSubmit} className="space-y-6 mt-6">
      <div className="space-y-2">
        <Label htmlFor="type">Type *</Label>
        <Input
          id="type"
          value={formData.type}
          onChange={(e) => {
            console.log('Type input changed:', e.target.value);
            setFormData(prev => ({ ...prev, type: e.target.value }));
          }}
          placeholder="e.g., camera, microphone, bluetooth"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => {
            console.log('Title input changed:', e.target.value);
            setFormData(prev => ({ ...prev, title: e.target.value }));
          }}
          placeholder="e.g., Camera Access"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => {
            console.log('Description changed:', e.target.value);
            setFormData(prev => ({ ...prev, description: e.target.value }));
          }}
          placeholder="Describe what this restriction controls..."
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category *</Label>
        <Input
          id="category"
          value={formData.category}
          onChange={(e) => {
            console.log('Category changed:', e.target.value);
            setFormData(prev => ({ ...prev, category: e.target.value }));
          }}
          placeholder="e.g., Privacy, Network, Device"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="default_value">Default Value</Label>
        <Select 
          value={formData.default_value} 
          onValueChange={(value) => {
            console.log('Default value changed:', value);
            setFormData(prev => ({ ...prev, default_value: value }));
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="allowed">Allowed</SelectItem>
            <SelectItem value="restricted">Restricted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="placement">Placement</Label>
        <Select 
          value={formData.placement} 
          onValueChange={(value) => {
            console.log('Placement changed:', value);
            setFormData(prev => ({ ...prev, placement: value }));
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="everywhere">Everywhere</SelectItem>
            <SelectItem value="device_only">Device Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3 pt-4">
        <Button 
          type="submit" 
          disabled={isLoading}
          className="flex-1"
        >
          <Save className="w-4 h-4 mr-2" />
          {isLoading ? 'Creating...' : 'Create Restriction'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export const AddRestrictionSheet = () => {
  console.log('AddRestrictionSheet component initializing');
  
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<AddRestrictionFormData>({
    type: '',
    title: '',
    description: '',
    category: '',
    default_value: 'allowed',
    placement: 'everywhere',
    parent_id: undefined
  });

  const resetForm = () => {
    console.log('Resetting form to initial state');
    setFormData({
      type: '',
      title: '',
      description: '',
      category: '',
      default_value: 'allowed',
      placement: 'everywhere',
      parent_id: undefined
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    
    if (!formData.type.trim() || !formData.title.trim() || !formData.category.trim()) {
      console.warn('Form validation failed - missing required fields');
      toast({
        title: "Error",
        description: "Please fill in all required fields (Type, Title, and Category)",
        variant: "destructive",
      });
      return;
    }

    // Since we're using hardcoded restrictions, we'll just show a message
    toast({
      title: "Info",
      description: "Restrictions are now managed through hardcoded configuration. Contact your administrator to add new restrictions.",
    });
    
    setOpen(false);
  };

  const handleCancel = () => {
    console.log('Form cancelled, closing sheet');
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    console.log('Sheet open state changing:', newOpen);
    setOpen(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  console.log('AddRestrictionSheet rendering with open:', open);

  return (
    <ErrorBoundary>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <Button onClick={() => console.log('Add Restriction button clicked')}>
            <Plus className="w-4 h-4 mr-2" />
            Add Restriction
          </Button>
        </SheetTrigger>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Add New Restriction</SheetTitle>
            <SheetDescription>
              Note: Restrictions are currently managed through hardcoded configuration. This form is for reference only.
            </SheetDescription>
          </SheetHeader>
          
          <ErrorBoundary>
            <AddRestrictionForm
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={false}
            />
          </ErrorBoundary>
        </SheetContent>
      </Sheet>
    </ErrorBoundary>
  );
};
